import { forwardRef, useState, type InputHTMLAttributes, type ReactNode } from 'react';
import { AlertCircle, ArrowRight, Eye, EyeOff, Lock, Mail, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { formatKgLocal, kgDigits } from './phone';

// 16px text on inputs: iOS Safari zooms the page on focus below that.
const INPUT =
  'auth-input w-full h-11 auth-tall:h-12 rounded-xl border bg-gray-50/70 text-base text-gray-900 placeholder:text-gray-400 outline-none transition duration-200 hover:bg-white focus:bg-white focus:ring-4';
const OK = 'border-gray-200 hover:border-gray-300 focus:border-blue-500 focus:ring-blue-500/10';
const BAD = 'border-red-300 bg-red-50/40 focus:border-red-400 focus:ring-red-500/10';
// Leading icon: tints with the field's state (needs `group` on the wrapper).
const ICON = 'pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 transition-colors';
const iconTone = (error?: string) => (error ? 'text-red-400' : 'text-gray-400 group-focus-within:text-blue-600');

function Field({
  id,
  label,
  error,
  hint,
  hintOnFocus,
  children,
}: {
  id: string;
  label: string;
  error?: string;
  hint?: ReactNode;
  /** Show the hint only while the field is focused (keeps the form one screen tall). */
  hintOnFocus?: boolean;
  children: ReactNode;
}) {
  return (
    <div className="group/field">
      <label htmlFor={id} className="mb-1 block text-[13px] font-semibold text-gray-700 auth-tall:mb-1.5 auth-tall:text-sm in-data-[compact]:auth-tiny:sr-only">
        {label}
      </label>
      {children}
      {error ? (
        <p id={`${id}-err`} role="alert" className="auth-fade-up mt-1 flex items-start gap-1.5 text-[12.5px] leading-snug text-red-600">
          <AlertCircle size={13} className="mt-px shrink-0" />
          {error}
        </p>
      ) : hint ? (
        <p className={`mt-1.5 text-[13px] text-gray-500 ${hintOnFocus ? 'auth-fade-up hidden group-focus-within/field:block' : ''}`}>{hint}</p>
      ) : null}
    </div>
  );
}

type InputProps = InputHTMLAttributes<HTMLInputElement> & { label: string; error?: string; hint?: ReactNode };

export const EmailField = forwardRef<HTMLInputElement, InputProps>(function EmailField({ label, error, hint, id = 'email', ...rest }, ref) {
  return (
    <Field id={id} label={label} error={error} hint={hint}>
      <div className="group relative">
        <Mail size={18} className={`${ICON} ${iconTone(error)}`} />
        <input
          ref={ref}
          id={id}
          type="email"
          inputMode="email"
          autoComplete="email"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          aria-invalid={!!error}
          aria-describedby={error ? `${id}-err` : undefined}
          className={`${INPUT} pl-11 pr-4 ${error ? BAD : OK}`}
          {...rest}
        />
      </div>
    </Field>
  );
});

export const NameField = forwardRef<HTMLInputElement, InputProps>(function NameField({ label, error, hint, id = 'name', ...rest }, ref) {
  return (
    <Field id={id} label={label} error={error} hint={hint}>
      <div className="group relative">
        <User size={18} className={`${ICON} ${iconTone(error)}`} />
        <input
          ref={ref}
          id={id}
          type="text"
          autoComplete="name"
          autoCapitalize="words"
          aria-invalid={!!error}
          aria-describedby={error ? `${id}-err` : undefined}
          className={`${INPUT} pl-11 pr-4 ${error ? BAD : OK}`}
          {...rest}
        />
      </div>
    </Field>
  );
});

export const PasswordField = forwardRef<HTMLInputElement, InputProps & { action?: ReactNode }>(function PasswordField(
  { label, error, hint, action, id = 'password', ...rest },
  ref,
) {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  return (
    <div>
      <div className="mb-1 flex items-baseline justify-between gap-3 auth-tall:mb-1.5 in-data-[compact]:auth-tiny:mb-0">
        <label htmlFor={id} className="block text-[13px] font-semibold text-gray-700 auth-tall:text-sm in-data-[compact]:auth-tiny:sr-only">
          {label}
        </label>
        {action ?? (hint && !error ? <span className="text-xs text-gray-400 in-data-[compact]:auth-tiny:hidden">{hint}</span> : null)}
      </div>
      <div className="group relative">
        <Lock size={18} className={`${ICON} ${iconTone(error)}`} />
        <input
          ref={ref}
          id={id}
          type={show ? 'text' : 'password'}
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          aria-invalid={!!error}
          aria-describedby={error ? `${id}-err` : undefined}
          className={`${INPUT} pl-11 pr-12 ${error ? BAD : OK}`}
          {...rest}
        />
        <button
          type="button"
          onClick={() => setShow((v) => !v)}
          className="absolute right-1.5 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-lg text-gray-400 transition hover:bg-gray-100 hover:text-blue-600"
          aria-label={show ? t('authFlow.hidePassword', 'Скрыть пароль') : t('authFlow.showPassword', 'Показать пароль')}
        >
          {show ? <EyeOff size={18} /> : <Eye size={18} />}
        </button>
      </div>
      {error ? (
        <p id={`${id}-err`} role="alert" className="auth-fade-up mt-1 flex items-start gap-1.5 text-[12.5px] leading-snug text-red-600">
          <AlertCircle size={13} className="mt-px shrink-0" />
          {error}
        </p>
      ) : hint && action ? (
        <p className="mt-1.5 text-[13px] text-gray-500">{hint}</p>
      ) : null}
    </div>
  );
});

/** Kyrgyz phone with a fixed +996 prefix; value is the 9 local digits. */
export function PhoneField({
  label,
  error,
  hint,
  value,
  onChange,
  onBlur,
  hintOnFocus,
  id = 'phone',
}: {
  label: string;
  error?: string;
  hint?: ReactNode;
  value: string;
  onChange: (digits: string) => void;
  onBlur?: () => void;
  hintOnFocus?: boolean;
  id?: string;
}) {
  return (
    <Field id={id} label={label} error={error} hint={hint} hintOnFocus={hintOnFocus}>
      <div
        className={`flex h-11 auth-tall:h-12 items-center overflow-hidden rounded-xl border bg-gray-50/70 transition duration-200 hover:bg-white focus-within:bg-white focus-within:ring-4 ${
          error
            ? 'border-red-300 bg-red-50/40 focus-within:border-red-400 focus-within:ring-red-500/10'
            : 'border-gray-200 hover:border-gray-300 focus-within:border-blue-500 focus-within:ring-blue-500/10'
        }`}
      >
        <span className="flex h-full shrink-0 items-center gap-1.5 border-r border-gray-200 pl-3.5 pr-3 text-base font-medium text-gray-700">
          <span aria-hidden>🇰🇬</span> +996
        </span>
        <input
          id={id}
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          placeholder="555 12 34 56"
          value={formatKgLocal(value)}
          onChange={(e) => onChange(kgDigits(e.target.value))}
          onBlur={onBlur}
          aria-invalid={!!error}
          aria-describedby={error ? `${id}-err` : undefined}
          className="h-full min-w-0 flex-1 bg-transparent px-3 text-base tracking-wide text-gray-900 outline-none placeholder:text-gray-400"
        />
      </div>
    </Field>
  );
}

export function FormAlert({ children, tone = 'error' }: { children: ReactNode; tone?: 'error' | 'info' | 'success' }) {
  const cls =
    tone === 'error'
      ? 'border-red-200 bg-red-50 text-red-700'
      : tone === 'success'
        ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
        : 'border-blue-200 bg-blue-50 text-blue-800';
  return (
    <div role={tone === 'error' ? 'alert' : 'status'} className={`auth-fade-up flex gap-2.5 rounded-xl border px-3.5 py-3 text-sm leading-relaxed ${cls}`}>
      <AlertCircle size={18} className="mt-px shrink-0" />
      <div className="min-w-0">{children}</div>
    </div>
  );
}

export function SubmitButton({ busy, children }: { busy?: boolean; children: ReactNode }) {
  return (
    <button
      type="submit"
      disabled={busy}
      aria-busy={busy || undefined}
      className="group relative flex h-11 auth-tall:h-12 w-full items-center justify-center gap-2 overflow-hidden rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-base font-bold text-white shadow-lg shadow-blue-600/25 transition-all duration-200 hover:-translate-y-px hover:shadow-xl hover:shadow-blue-600/30 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-blue-500/30 active:translate-y-0 active:scale-[.99] disabled:translate-y-0 disabled:opacity-70"
    >
      {/* Sheen sweeping across on hover. */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-y-0 -left-1/2 w-1/3 -skew-x-12 bg-white/20 opacity-0 transition-all duration-700 group-hover:left-[120%] group-hover:opacity-100"
      />
      {busy ? (
        <span className="auth-spinner h-4 w-4 rounded-full border-2 border-white/40 border-t-white" />
      ) : null}
      <span className="relative">{children}</span>
      {!busy && <ArrowRight size={18} className="relative transition-transform duration-200 group-hover:translate-x-0.5" />}
    </button>
  );
}
